import { t } from '@lingui/macro'
import { useState } from 'react'
import { useTheme } from 'styled-components'

import { Flex, FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { StObservationItem } from '@/modules/shared/components/observation-item/ObservationItem'
import { type DialogState, StructuredObservationListContext } from './StructuredObservationList.context'

import type { ReactNode } from 'react'

type StructuredObservationListProps<T> = {
  items: T[]
  getId: (item: T) => string
  getName: (item: T) => string
  getSubtitle: (item: T) => string | undefined
  /** Rendered under the row, across the full card, for entries that need a marker. */
  getBadge?: (item: T) => ReactNode
  /** Its own section between the list and the actions. */
  attachments?: ReactNode
  onDelete: (id: string) => void
  addLabel: string
  onSubmitAndAdvance: () => void
  isSubmitDisabled: boolean
  emptyMessage?: string
  children: ReactNode
}

export const StructuredObservationList = <T,>({
  items,
  getId,
  getName,
  getSubtitle,
  getBadge,
  attachments,
  onDelete,
  addLabel,
  onSubmitAndAdvance,
  isSubmitDisabled,
  emptyMessage,
  children,
}: StructuredObservationListProps<T>) => {
  const theme = useTheme()

  const [dialog, setDialog] = useState<DialogState>({ mode: 'closed' })

  const openInsert = () => setDialog({ mode: 'insert' })
  const openUpdate = (id: string) => setDialog({ mode: 'update', itemId: id })
  const closeDialog = () => setDialog({ mode: 'closed' })

  return (
    <StructuredObservationListContext.Provider value={{ dialog, openInsert, openUpdate, closeDialog }}>
      <FlexColumn gap={theme.UI.Spacing.Space6}>
        {/* Item list */}
        {items.length > 0 ? (
          <FlexColumn gap={2}>
            {items.map((item) => {
              const id = getId(item)
              const name = getName(item)
              const subtitle = getSubtitle(item)
              const badge = getBadge?.(item)

              return (
                <StObservationItem
                  key={id}
                  type='BodyMediumRegular'
                  style={{
                    padding: `${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space2} ${theme.UI.SpacingPx.Space3} ${theme.UI.SpacingPx.Space4}`,
                  }}
                >
                  <FlexColumn gap={theme.UI.Spacing.Space3}>
                    <Flex
                      flexDirection='row'
                      justifyContent='space-between'
                      alignItems='flex-start'
                      gap={theme.UI.Spacing.Space4}
                    >
                      <FlexColumn gap={theme.UI.Spacing.Space1}>
                        <Span type='BodyMediumBold' color={theme.Colors.Neutrals.Darker}>
                          {name}
                        </Span>
                        {subtitle && (
                          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
                            {subtitle}
                          </Span>
                        )}
                      </FlexColumn>
                      <FlexRow>
                        <IconButton
                          type='button'
                          size='large'
                          icon={['far', 'pen']}
                          variant='Tertiary'
                          onClick={() => openUpdate(id)}
                        />
                        <IconButton
                          size='large'
                          type='button'
                          icon={['fal', 'trash-can']}
                          variant='Tertiary'
                          color={theme.Colors.Status.Error}
                          onClick={() => onDelete(id)}
                        />
                      </FlexRow>
                    </Flex>
                    {badge}
                  </FlexColumn>
                </StObservationItem>
              )
            })}
          </FlexColumn>
        ) : emptyMessage ? (
          <StObservationItem type='BodyMediumRegular'>{emptyMessage}</StObservationItem>
        ) : null}

        {/* Attachments */}
        {attachments}

        {/* Add button */}
        <Button variant='Secondary' type='button' onClick={openInsert} prefixIcon={['fal', 'plus']}>
          {addLabel}
        </Button>

        {/* Continue */}
        <Button type='button' variant='Primary' onClick={onSubmitAndAdvance} disabled={isSubmitDisabled}>
          {t({
            id: 'globals.continue',
            comment: 'Global for continue',
            message: 'Ga verder',
          })}
        </Button>

        {/* Dialog (rendered by children via ItemDialogue) */}
        {children}
      </FlexColumn>
    </StructuredObservationListContext.Provider>
  )
}
